import React from 'react';
import { Card, Icon, IconDefinitions, SpinnerDefault } from '../../../components';
import { BookingEventModel } from '../../../data/entities/booking/BookingEventModel';
import { to12HourTime } from '../../../utils/dateUtils';
import providerStyle from '../styles/Provider.module.scss';
import { formatPrice, getChargeLabel } from '../../../utils';
import { useAsync } from '../../../hooks';
import { ServiceLocationService } from '../../../services';

interface BookingEventItemProps {
  event: BookingEventModel;
  locationId: number;
  onClick?: () => void;
  onDelete: (event: BookingEventModel) => void;
}

const BookingEventItem: React.FC<BookingEventItemProps> = ({ event, locationId, onDelete, onClick }) => {
  const apiBaseUrl = process.env.REACT_APP_API_URL;
  const serviceLocationService = new ServiceLocationService();

  const { value: servicePriceInfo, loading } = useAsync(
    () => {
      if (!event.serviceId || !locationId) return Promise.resolve(null);
      return serviceLocationService.getEffectiveServicePriceAsync(event.serviceId, locationId);
    },
    [event.serviceId, locationId]
  );

  const chargeAmount = servicePriceInfo?.finalChargeAmount ?? 0;
  const effectivePrice = servicePriceInfo?.effectivePrice || event.service?.defaultPrice || 0;
  const duration = event.service?.defaultDuration || 0;

  return (
    <Card className="mb-3" onClick={onClick}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="fw-bold mb-0">{event.service?.name}</h5>
        <Icon
          className={`cursor-pointer ${providerStyle.hover} text-danger`}
          iconName={IconDefinitions.x}
          onClick={() => onDelete(event)}
        />
      </div>

      {!event.service?.hideProviders && (event.provider?.name || event.isAnyProvider) && (
        <div className="d-flex mb-2">
          {event.isAnyProvider ? (
            <div
              className={`${providerStyle.providerImage} d-flex align-items-center justify-content-center rounded-circle flex-shrink-0`}
              style={{ backgroundColor: '#f7bc9e' }}
            >
              <i className="fa fa-users small text-white" />
            </div>
          ) : (
            <img
              src={`${apiBaseUrl}provider/${event.provider?.id}/profilepicture`}
              alt={event.provider?.name}
              className={providerStyle.providerImage}
            />
          )}
          <div className="d-flex align-items-center">
            <span className="text-muted small">with {event.provider?.name ?? 'Any Provider'}</span>
          </div>
        </div>
      )}

      {/* Time Range and Duration */}
      {event.startTime ? (
        <div className="mb-2">
          <span className="text-muted small">
            <i className="fa fa-clock me-2"></i>
            {to12HourTime(event.startTime)}
            {duration > 0 && <span className="ms-2">({duration} min)</span>}
          </span>
        </div>
      ) : null}
      
      {/* Price Summary */}
      {loading ? (
        <div className="text-center py-2">
          <SpinnerDefault />
        </div>
      ) : (
        <div className="mt-3 pt-2 border-top">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span className="text-muted small">Service Price</span>
            <span>{formatPrice(effectivePrice)}</span>
          </div>

          <div className="d-flex justify-content-between align-items-center">
            <span className="text-primary small">
              <i className="fa fa-arrow-right me-1"></i>
              {getChargeLabel(servicePriceInfo?.appliedChargeType)}
            </span>
            <span className="text-primary fw-bold">{formatPrice(chargeAmount)}</span>
          </div>
        </div>
      )}
    </Card>
  );
};

export default BookingEventItem;